import React, { useState, useEffect } from 'react';
import { useReservation } from '../../hooks/useReservation';
import { useReservationPDFShare } from '../../hooks/useReservationPDFShare';
import { useNotification } from '../../hooks/useNotification';
import Layout from '../../components/Layout/Layout';
import ReservationPDF from '../../components/Reservation/ReservationPDF';
import {
    FaSearch,
    FaCheckCircle,
    FaClock,
    FaTimesCircle,
    FaDownload,
    FaPhone,
    FaCalendarAlt,
    FaCut,
    FaDollarSign,
    FaSort,
    FaTimes,
    FaChevronLeft,
    FaChevronRight,
    FaInbox,
    FaFileAlt,
    FaListUl
} from 'react-icons/fa';
import { BsWhatsapp } from 'react-icons/bs';
import styles from './styles/ReservationManagementPage.module.css';

const ITEMS_PER_PAGE = 10;

export default function ReservationManagementPage() {
    const { getAllReservations, updateReservation } = useReservation();
    const { openConfirmationWhatsApp, shareReservationPDF, loading: pdfLoading } = useReservationPDFShare();
    const { showNotification } = useNotification();

    const [reservations, setReservations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedReservation, setSelectedReservation] = useState(null);
    const [filter, setFilter] = useState('pending');
    const [showPDFContainer, setShowPDFContainer] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('date');
    const [currentPage, setCurrentPage] = useState(1);
    const [activeAction, setActiveAction] = useState(null);

    // Récupérer les réservations
    useEffect(() => {
        const fetchReservations = async () => {
            try {

                setLoading(true);

                const res = await getAllReservations();

                setReservations(res);
                setCurrentPage(1);
            } catch (error) {
                console.error('Erreur:', error);
                showNotification('Erreur lors du chargement des réservations', 'error');
            } finally {
                setLoading(false);
            }
        };

        fetchReservations();
    }, [getAllReservations, showNotification]);

    // Filtrer et rechercher les réservations
    const filteredReservations = reservations
        .filter(res => res.status === filter)
        .filter(res =>
            res.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            res.clientEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
            res.clientPhone.includes(searchTerm)
        )
        .sort((a, b) => {
            if (sortBy === 'date') {
                return new Date(b.date) - new Date(a.date);
            }
            if (sortBy === 'price') {
                return b.price - a.price;
            }
            return 0;
        });

    // Pagination
    const totalPages = Math.ceil(filteredReservations.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const paginatedReservations = filteredReservations.slice(startIndex, endIndex);

    // Réinitialiser la page quand le filtre change
    useEffect(() => {
        setCurrentPage(1);
    }, [filter, searchTerm, sortBy]);

    // Confirmer la réservation et envoyer le PDF
    const handleConfirmAndSendPDF = async (reservation) => {
        let whatsappWindow = null;
        setActiveAction(`confirm-${reservation.id}`);
        try {
            const targetPhone = reservation.clientPhone?.trim();

            if (!targetPhone || targetPhone === 'Téléphone inconnu') {
                throw new Error('Numéro client introuvable. Le client doit renseigner son téléphone WhatsApp dans son profil.');
            }

            // Pré-ouvre la fenêtre pour éviter le blocage popup après appels async
            whatsappWindow = window.open('', '_blank');
            showNotification(`Preparation WhatsApp vers ${targetPhone}`, 'info');

            await updateReservation(reservation.id, {
                status: 'confirmée',
                confirmedAt: new Date().toISOString(),
            });

            setReservations(prev =>
                prev.map(res =>
                    res.id === reservation.id
                        ? { ...res, status: 'confirmée' }
                        : res
                )
            );

            setSelectedReservation(reservation);
            setShowPDFContainer(true);

            await openConfirmationWhatsApp(
                reservation,
                targetPhone,
                reservation.clientName,
                whatsappWindow
            );

            const shareResult = await shareReservationPDF(
                reservation,
                reservation.clientName
            );

            setShowPDFContainer(false);
            setSelectedReservation(null);

            showNotification(
                shareResult.cancelled
                    ? 'Partage annule. Reservation confirmee et WhatsApp ouvert.'
                    : shareResult.shared
                        ? 'Reservation confirmee. WhatsApp ouvert et PDF partage.'
                        : 'Reservation confirmee. WhatsApp ouvert et PDF telecharge.',
                'success'
            );
        } catch (error) {
            console.error('Erreur:', error);
            if (whatsappWindow && !whatsappWindow.closed) {
                whatsappWindow.close();
            }
            setShowPDFContainer(false);
            setSelectedReservation(null);
            showNotification(`Échec envoi WhatsApp/PDF: ${error.message}`, 'error');
        } finally {
            setActiveAction(null);
        }
    };

    // Renvoyer le PDF
    const handleResendPDF = async (reservation) => {
        let whatsappWindow = null;
        setActiveAction(`resend-${reservation.id}`);
        try {
            const targetPhone = reservation.clientPhone?.trim();

            if (!targetPhone || targetPhone === 'Téléphone inconnu') {
                throw new Error('Numéro client introuvable.');
            }

            whatsappWindow = window.open('', '_blank');
            showNotification(`Preparation WhatsApp vers ${targetPhone}`, 'info');

            setSelectedReservation(reservation);
            setShowPDFContainer(true);

            await openConfirmationWhatsApp(
                reservation,
                targetPhone,
                reservation.clientName,
                whatsappWindow
            );

            const shareResult = await shareReservationPDF(
                reservation,
                reservation.clientName
            );

            setShowPDFContainer(false);
            setSelectedReservation(null);

            showNotification(
                shareResult.cancelled
                    ? 'Partage annule. WhatsApp ouvert.'
                    : shareResult.shared
                        ? 'WhatsApp ouvert et PDF partage.'
                        : 'WhatsApp ouvert et PDF telecharge.',
                'success'
            );
        } catch (error) {
            console.error('Erreur:', error);
            if (whatsappWindow && !whatsappWindow.closed) {
                whatsappWindow.close();
            }
            setShowPDFContainer(false);
            setSelectedReservation(null);
            showNotification(`Échec renvoi PDF: ${error.message}`, 'error');
        } finally {
            setActiveAction(null);
        }
    };

    // Generer le PDF puis ouvrir le partage multi-canaux (WhatsApp, email, etc.)
    const handleDownloadOrSharePDF = async (reservation) => {
        setActiveAction(`share-${reservation.id}`);
        try {
            setSelectedReservation(reservation);
            setShowPDFContainer(true);

            const shareResult = await shareReservationPDF(
                reservation,
                reservation.clientName
            );

            setShowPDFContainer(false);
            setSelectedReservation(null);

            showNotification(
                shareResult.cancelled
                    ? 'Partage annule par l utilisateur.'
                    : shareResult.shared
                        ? 'PDF genere et partage via le menu de partage.'
                        : 'PDF genere et telecharge sur cet appareil.',
                'success'
            );
        } catch (error) {
            console.error('Erreur:', error);
            setShowPDFContainer(false);
            setSelectedReservation(null);
            showNotification(`Echec generation/partage PDF: ${error.message}`, 'error');
        } finally {
            setActiveAction(null);
        }
    };

    // Annuler la réservation
    const handleCancelReservation = async (reservation) => {
        try {

            if (!window.confirm('Êtes-vous sûr de vouloir annuler cette réservation?')) {
                return;
            }

            await updateReservation(reservation.id, {
                status: 'annulée',
            });

            setReservations(prev =>
                prev.map(res =>
                    res.id === reservation.id
                        ? { ...res, status: 'annulée' }
                        : res
                )
            );

            showNotification('Réservation annulée', 'success');

        } catch (error) {
            console.error('Erreur:', error);
            showNotification('Erreur lors de l\'annulation', 'error');
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'pending':
                return <FaClock />;
            case 'confirmée':
                return <FaCheckCircle />;
            case 'annulée':
                return <FaTimesCircle />;
            default:
                return null;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending':
                return 'warning';
            case 'confirmée':
                return 'success';
            case 'annulée':
                return 'danger';
            default:
                return 'default';
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className={styles.container}>
                    <div className={styles.loadingState}>
                        <div className={styles.spinner}></div>
                        <p>Chargement des réservations...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    const stats = {
        pending: reservations.filter(r => r.status === 'pending').length,
        confirmée: reservations.filter(r => r.status === 'confirmée').length,
        annulée: reservations.filter(r => r.status === 'annulée').length,
        total: reservations.length,
    };

    return (
        <Layout>
            <div className={styles.adminContainer}>
                {/* PDF Container */}
                {showPDFContainer && selectedReservation && (
                    <div
                        id="reservation-pdf"
                        style={{
                            position: 'fixed',
                            left: '-9999px',
                            top: '-9999px',
                            width: '210mm',
                            height: 'auto',
                            background: 'white',
                            zIndex: -1,
                            overflow: 'visible',
                            padding: '20mm',
                        }}
                    >
                        <ReservationPDF
                            reservation={selectedReservation}
                            clientName={selectedReservation.clientName}
                            clientEmail={selectedReservation.clientEmail}
                        />
                    </div>
                )}

                {/* Header */}
                <div className={styles.adminHeader}>
                    <div className={styles.headerContent}>
                        <div className={styles.headerIcon}>
                            <FaListUl />
                        </div>
                        <div>
                            <h1 className={styles.title}>Gestion des Réservations</h1>
                            <p className={styles.subtitle}>Administrez et confirmez les réservations clients</p>
                        </div>
                    </div>
                </div>

                {/* Stats Overview */}
                <div className={styles.statsOverview}>
                    <div className={`${styles.statBox} ${styles.total}`}>
                        <div className={styles.statIcon}>
                            <FaListUl />
                        </div>
                        <div>
                            <div className={styles.statNumber}>{stats.total}</div>
                            <div className={styles.statLabel}>Total</div>
                        </div>
                    </div>
                    <div className={`${styles.statBox} ${styles.pending}`}>
                        <div className={styles.statIcon}>
                            <FaClock />
                        </div>
                        <div>
                            <div className={styles.statNumber}>{stats.pending}</div>
                            <div className={styles.statLabel}>En attente</div>
                        </div>
                    </div>
                    <div className={`${styles.statBox} ${styles.confirmed}`}>
                        <div className={styles.statIcon}>
                            <FaCheckCircle />
                        </div>
                        <div>
                            <div className={styles.statNumber}>{stats.confirmée}</div>
                            <div className={styles.statLabel}>Confirmées</div>
                        </div>
                    </div>
                    <div className={`${styles.statBox} ${styles.cancelled}`}>
                        <div className={styles.statIcon}>
                            <FaTimesCircle />
                        </div>
                        <div>
                            <div className={styles.statNumber}>{stats.annulée}</div>
                            <div className={styles.statLabel}>Annulées</div>
                        </div>
                    </div>
                </div>

                {/* Controls */}
                <div className={styles.controls}>
                    {/* Search */}
                    <div className={styles.searchBox}>
                        <FaSearch className={styles.searchIcon} />
                        <input
                            type="text"
                            placeholder="Rechercher par nom, email ou téléphone..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className={styles.searchInput}
                        />
                    </div>

                    {/* Filters */}
                    <div className={styles.filterButtons}>
                        <button
                            className={`${styles.filterBtn} ${filter === 'pending' ? styles.active : ''}`}
                            onClick={() => setFilter('pending')}
                        >
                            <FaClock /> En attente
                        </button>
                        <button
                            className={`${styles.filterBtn} ${filter === 'confirmée' ? styles.active : ''}`}
                            onClick={() => setFilter('confirmée')}
                        >
                            <FaCheckCircle /> Confirmées
                        </button>
                        <button
                            className={`${styles.filterBtn} ${filter === 'annulée' ? styles.active : ''}`}
                            onClick={() => setFilter('annulée')}
                        >
                            <FaTimesCircle /> Annulées
                        </button>
                    </div>

                    {/* Sort */}
                    <div className={styles.sortBox}>
                        <FaSort className={styles.sortIcon} />
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className={styles.sortSelect}
                        >
                            <option value="date">Trier par date</option>
                            <option value="price">Trier par prix</option>
                        </select>
                    </div>
                </div>

                {/* Reservations List */}
                <div className={styles.reservationsContainer}>
                    {filteredReservations.length === 0 ? (
                        <div className={styles.emptyState}>
                            <div className={styles.emptyIcon}>
                                <FaInbox />
                            </div>
                            <h3>Aucune réservation</h3>
                            <p>Il n'y a pas de réservation avec le filtre sélectionné</p>
                        </div>
                    ) : (
                        <>
                            <div className={styles.reservationsList}>
                                {paginatedReservations.map((reservation) => (
                                    <div
                                        key={reservation.id}
                                        className={`${styles.reservationCard} ${styles[getStatusColor(reservation.status)]}`}
                                    >
                                        {/* Card Header */}
                                        <div className={styles.cardTop}>
                                            <div className={styles.clientSection}>
                                                <div className={styles.clientAvatar}>
                                                    {reservation.clientName.charAt(0).toUpperCase()}
                                                </div>
                                                <div className={styles.clientDetails}>
                                                    <h3 className={styles.clientName}>{reservation.clientName}</h3>
                                                    <p className={styles.clientEmail}>{reservation.clientEmail}</p>
                                                    <p className={styles.clientPhone}>
                                                        <FaPhone /> {reservation.clientPhone}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className={styles.statusSection}>
                                                <span className={`${styles.statusBadge} ${styles[reservation.status]}`}>
                                                    {getStatusIcon(reservation.status)}
                                                    {reservation.status === 'pending' && 'En attente'}
                                                    {reservation.status === 'confirmée' && 'Confirmée'}
                                                    {reservation.status === 'annulée' && 'Annulée'}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Card Details */}
                                        <div className={styles.cardDetails}>
                                            <div className={styles.detailRow}>
                                                <div className={styles.detailItem}>
                                                    <FaCut className={styles.detailIcon} />
                                                    <div>
                                                        <span className={styles.detailLabel}>Service</span>
                                                        <span className={styles.detailValue}>{reservation.serviceName}</span>
                                                    </div>
                                                </div>
                                                <div className={styles.detailItem}>
                                                    <FaCalendarAlt className={styles.detailIcon} />
                                                    <div>
                                                        <span className={styles.detailLabel}>Date</span>
                                                        <span className={styles.detailValue}>
                                                            {new Date(reservation.date).toLocaleDateString('fr-FR')}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className={styles.detailRow}>
                                                <div className={styles.detailItem}>
                                                    <FaClock className={styles.detailIcon} />
                                                    <div>
                                                        <span className={styles.detailLabel}>Heure</span>
                                                        <span className={styles.detailValue}>{reservation.time}</span>
                                                    </div>
                                                </div>
                                                <div className={styles.detailItem}>
                                                    <FaDollarSign className={styles.detailIcon} />
                                                    <div>
                                                        <span className={styles.detailLabel}>Prix</span>
                                                        <span className={styles.detailValue}>{reservation.price}€</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {reservation.notes && (
                                                <div className={styles.notesBox}>
                                                    <FaFileAlt className={styles.notesIcon} />
                                                    <div>
                                                        <strong>Notes:</strong> {reservation.notes}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Card Actions */}
                                        <div className={styles.cardActions}>
                                            {reservation.status === 'pending' && (
                                                <>
                                                    <button
                                                        className={`${styles.actionBtn} ${styles.success}`}
                                                        onClick={() => handleConfirmAndSendPDF(reservation)}
                                                        disabled={pdfLoading}
                                                        title="Confirmer et envoyer le PDF via WhatsApp"
                                                    >
                                                        {activeAction === `confirm-${reservation.id}` ? (
                                                            <>
                                                                <span className={styles.buttonSpinner}></span>
                                                                Generation du PDF...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <BsWhatsapp /> Confirmer & Envoyer
                                                            </>
                                                        )}
                                                    </button>
                                                    <button
                                                        className={`${styles.actionBtn} ${styles.info}`}
                                                        onClick={() => handleDownloadOrSharePDF(reservation)}
                                                        disabled={pdfLoading}
                                                        title="Generer puis partager le PDF (WhatsApp, email, etc.)"
                                                    >
                                                        {activeAction === `share-${reservation.id}` ? (
                                                            <>
                                                                <span className={styles.buttonSpinner}></span>
                                                                Generation du PDF...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <FaDownload /> Telecharger / Partager PDF
                                                            </>
                                                        )}
                                                    </button>
                                                    <button
                                                        className={`${styles.actionBtn} ${styles.danger}`}
                                                        onClick={() => handleCancelReservation(reservation)}
                                                        title="Annuler cette réservation"
                                                    >
                                                        <FaTimes /> Annuler
                                                    </button>
                                                </>
                                            )}
                                            {reservation.status === 'confirmée' && (
                                                <>
                                                    <button
                                                        className={`${styles.actionBtn} ${styles.info}`}
                                                        onClick={() => handleResendPDF(reservation)}
                                                        disabled={pdfLoading}
                                                        title="Renvoyer le message WhatsApp et partager le PDF"
                                                    >
                                                        {activeAction === `resend-${reservation.id}` ? (
                                                            <>
                                                                <span className={styles.buttonSpinner}></span>
                                                                Generation du PDF...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <FaDownload /> Renvoyer PDF
                                                            </>
                                                        )}
                                                    </button>
                                                    <button
                                                        className={`${styles.actionBtn} ${styles.info}`}
                                                        onClick={() => handleDownloadOrSharePDF(reservation)}
                                                        disabled={pdfLoading}
                                                        title="Generer puis partager le PDF (WhatsApp, email, etc.)"
                                                    >
                                                        {activeAction === `share-${reservation.id}` ? (
                                                            <>
                                                                <span className={styles.buttonSpinner}></span>
                                                                Generation du PDF...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <FaDownload /> Telecharger / Partager PDF
                                                            </>
                                                        )}
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className={styles.pagination}>
                                    <button
                                        className={styles.paginationBtn}
                                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                        disabled={currentPage === 1}
                                    >
                                        <FaChevronLeft /> Précédent
                                    </button>

                                    <div className={styles.pageNumbers}>
                                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                            <button
                                                key={page}
                                                className={`${styles.pageNumber} ${currentPage === page ? styles.active : ''}`}
                                                onClick={() => setCurrentPage(page)}
                                            >
                                                {page}
                                            </button>
                                        ))}
                                    </div>

                                    <button
                                        className={styles.paginationBtn}
                                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                        disabled={currentPage === totalPages}
                                    >
                                        Suivant <FaChevronRight />
                                    </button>

                                    <div className={styles.paginationInfo}>
                                        Page {currentPage} sur {totalPages} ({filteredReservations.length} résultats)
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </Layout>
    );
}
